import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Camera, Mic, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  getSubTouchpointColor,
  calculateScore,
  calculateWeightedScore,
} from '@/lib/shared-data';
import { useParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/providers/AuthProvider'; 
import AxiosClient from '@/api/client';
import { API_BASE_URL } from '@/config/constants';

interface AssessmentStandard {
  id: number;
  question: string;
  subTouchpointTag: string;
  status: 'yes' | 'no' | 'na' | null;
  notes: string;
  weight?: number;
}

interface AssessmentTitle {
  id: number;
  name: string;
  standards: AssessmentStandard[];
}

interface AssessmentContentProps {
  selectedTouchpointName: string;
  titles: AssessmentTitle[];
  onStandardStatusChange: (
    titleId: number,
    standardId: number,
    status: 'yes' | 'no' | 'na'
  ) => void;
  onStandardNotesChange: (
    titleId: number,
    standardId: number,
    notes: string
  ) => void;
  onTouchpointTimeChange: (answeredAt: string) => void;
  onMediaUpload: (type: 'photo' | 'audio') => void;
  touchpointAnsweredAt?: string;
  className?: string;
  isLoading?: boolean;
  currentImagesCount?: number;
  currentAudiosCount?: number;
  maxImages?: number;
  maxAudios?: number;
  departmentId?: number;
  touchpointId?: number;
  onMediaIconClick?: () => void;
  isReadOnly?: boolean;
}

export function AssessmentContent({
  selectedTouchpointName,
  titles,
  onStandardStatusChange,
  onStandardNotesChange,
  onTouchpointTimeChange,
  onMediaUpload,
  touchpointAnsweredAt,
  className,
  isLoading = false,
  currentImagesCount = 0,
  currentAudiosCount = 0,
  maxImages = 24,
  maxAudios = 5,
  departmentId,
  touchpointId,
  onMediaIconClick,
  isReadOnly = false,
}: AssessmentContentProps) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { id: assessmentId } = useParams<{ id: string }>();
  const { toast } = useToast();
  const { hasRole } = useAuth();
  const canExport = !hasRole('auditor') && !isReadOnly;

  // Scroll to top when touchpoint changes
  useEffect(() => {
    if (selectedTouchpointName) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selectedTouchpointName]);

  // Calculate metrics for all standards
  const getScoreMetrics = () => {
    const allStandards = titles.flatMap(title => title.standards);

    // Check if any standards have weights defined
    const hasWeights = allStandards.some(
      standard => standard.weight !== undefined && standard.weight > 0
    );

    if (hasWeights) {
      return { ...calculateWeightedScore(allStandards), isWeighted: true };
    } else {
      return { ...calculateScore(allStandards), isWeighted: false };
    }
  };

  const metrics = getScoreMetrics();

  // PDF Export function
  const exportPDF = async () => {
    if (!assessmentId || !departmentId || !touchpointId) {
      toast({
        title: t('common.error'),
        description: t(
          'assessments.perform.missingData',
          'Missing assessment data'
        ),
        variant: 'destructive',
      });
      return;
    }

    try {
      const endpoint = `/assessments/${assessmentId}/department/${departmentId}/touchpoint/${touchpointId}/export-pdf`;

      // Create AxiosClient with token support
      const client = new AxiosClient({
        endpoint: endpoint,
        contentType: 'application/json',
        isToken: true, // Token will be automatically added by AxiosClient
        baseURL: API_BASE_URL,
      });

      // Make request using AxiosClient instance with the endpoint
      const response = await client.instance.get(endpoint, {
        responseType: 'blob', // Important for file download
        headers: {
          Accept: 'application/pdf',
        },
      });

      // Download file
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `assessment-${assessmentId}-touchpoint-${touchpointId}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);

      toast({
        title: t('common.success'),
        description: t(
          'assessments.perform.pdfExported',
          'PDF exported successfully'
        ),
      });
    } catch (error) {
      console.error('PDF export failed:', error);
      toast({
        title: t('common.error'),
        description: t(
          'assessments.perform.exportFailed',
          'Failed to export PDF'
        ),
        variant: 'destructive',
      });
    }
  };

  // Show loading state when fetching data
  if (isLoading) {
    return (
      <div
        className={cn(
          'flex-1 p-4 sm:p-6 flex items-center justify-center',
          className
        )}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-sm text-gray-500">
            {t('common.loading', 'Loading...')}
          </p>
        </div>
      </div>
    );
  }

  if (!selectedTouchpointName) {
    return (
      <div
        className={cn(
          'flex-1 p-4 sm:p-6 flex items-center justify-center',
          className
        )}
      >
        <div className="text-center text-gray-500 max-w-md mx-auto">
          <div className="mb-4 text-4xl">📋</div>
          <h3 className="text-base sm:text-lg font-medium mb-2">
            {t('assessments.perform.selectTouchpoint', 'Select a Touchpoint')}
          </h3>
          <p className="text-sm text-gray-400">
            {t(
              'assessments.perform.selectTouchpointDescription',
              'Choose a touchpoint from the sidebar to begin your assessment'
            )}
          </p>

          {/* Mobile hint */}
          <div className="lg:hidden mt-4">
            <p className="text-xs text-gray-400">
              {t(
                'assessments.perform.tapMenuHint',
                'Tap the menu button to open navigation'
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Empty state when no titles
  if (titles.length === 0) {
    return (
      <div
        className={cn(
          'flex-1 p-4 sm:p-6 flex items-center justify-center',
          className
        )}
      >
        <div className="text-center text-gray-500 max-w-md mx-auto">
          <div className="mb-4 text-4xl">📭</div>
          <h3 className="text-base sm:text-lg font-medium mb-2">
            {t('assessments.perform.noStandards', 'No Standards Available')}
          </h3>
          <p className="text-sm text-gray-400">
            {t(
              'assessments.perform.noStandardsDescription',
              'There are no standards configured for this touchpoint.'
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex-1 p-3 sm:p-4 lg:p-6 relative', className)}>
      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3"></div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        {/* Header with Score and Media Upload */}
        <div className="mb-4 sm:mb-6">
          <div className="mb-3 sm:mb-4">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900 truncate">
              {t('assessments.perform.assessment')} - {selectedTouchpointName}
            </h1>
          </div>

          {/* Score Display - Mobile Optimized */}
          <div className="space-y-3 sm:space-y-4">
            <div
              className={cn(
                'flex flex-col xs:flex-row xs:items-center gap-3 xs:gap-4',
                isRTL ? 'xs:flex-row-reverse' : 'xs:flex-row'
              )}
            >
              <div className="flex items-center gap-2">
                <span className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
                  {metrics.score}%
                </span>
                <span className="text-gray-600 text-xs sm:text-sm">
                  {t('assessments.perform.score')}
                </span>
              </div>

              <div  className="flex flex-wrap gap-1 xs:gap-2">
                <Badge className="bg-green-500 text-white px-2 py-1 text-xs">
                  {metrics.yes} {t('assessments.perform.yes')}
                </Badge>
                <Badge className="bg-red-500 text-white px-2 py-1 text-xs">
                  {metrics.no} {t('assessments.perform.no')}
                </Badge>
                <Badge className="bg-gray-500 text-white px-2 py-1 text-xs">
                  {metrics.na} {t('assessments.perform.na')}
                </Badge>
              </div>
            </div>

            {/* Action Buttons - Aligned in one row */}
            <div className="flex items-end justify-between gap-2 overflow-x-auto pb-1 -mx-1 px-1 flex-wrap ">
              {/* Left side - Action buttons */}
              <div className="flex gap-2 items-end">
                {canExport && (
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={exportPDF}
                      className="flex items-center gap-1 text-xs whitespace-nowrap flex-shrink-0 min-h-[44px] h-11"
                    >
                      <FileText className="w-3 h-3" />
                      {t('assessments.perform.exportTouchpoint', 'Export Touch-point')}
                    </Button>
                    <div className="h-[20px]"></div>
                  </div>
                )}
                <div className="flex flex-col gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onMediaUpload('photo')}
                    className="flex items-center gap-1 text-xs whitespace-nowrap flex-shrink-0 min-h-[44px] h-11"
                    disabled={isReadOnly || currentImagesCount >= maxImages}
                  >
                    <Camera className="w-3 h-3" />
                    {t('assessments.perform.addPhoto')}
                  </Button>
                  <div className="flex items-center justify-center gap-1">
                    {onMediaIconClick && currentImagesCount > 0 && (
                      <button
                        onClick={onMediaIconClick}
                        className="hover:bg-gray-100 rounded-full p-0.5 transition-colors"
                      >
                        <AlertCircle className="w-3 h-3 text-blue-600" />
                      </button>
                    )}
                    <span
                      className={cn(
                        'text-[10px] font-medium text-center',
                        currentImagesCount >= maxImages
                          ? 'text-red-600'
                          : 'text-gray-500'
                      )}
                    >
                      {currentImagesCount}/{maxImages}{' '}
                      {t('assessments.perform.images', 'images')}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onMediaUpload('audio')}
                    className="flex items-center gap-1 text-xs whitespace-nowrap flex-shrink-0 min-h-[44px] h-11"
                    disabled={isReadOnly || currentAudiosCount >= maxAudios}
                  >
                    <Mic className="w-3 h-3" />
                    {t('assessments.perform.addAudio')}
                  </Button>
                  <div className="flex items-center justify-center gap-1">
                    {onMediaIconClick && currentAudiosCount > 0 && (
                      <button
                        onClick={onMediaIconClick}
                        className="hover:bg-gray-100 rounded-full p-0.5 transition-colors"
                      >
                        <AlertCircle className="w-3 h-3 text-blue-600" />
                      </button>
                    )}
                    <span
                      className={cn(
                        'text-[10px] font-medium text-center',
                        currentAudiosCount >= maxAudios
                          ? 'text-red-600'
                          : 'text-gray-500'
                      )}
                    >
                      {currentAudiosCount}/{maxAudios}{' '}
                      {t('assessments.perform.audios', 'audios')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right side - Touchpoint Time Input */}
              <div className="flex flex-col gap-1 ml-auto">
                <label
                  htmlFor="touchpoint-time"
                  className="text-xs font-medium text-gray-700 flex items-center gap-1 whitespace-nowrap"
                >
                  {t('assessments.perform.answeredAt', 'Date & Time')}
                  <span className="text-red-500">*</span>
                </label>
                <Input
                  id="touchpoint-time"
                  type="datetime-local"
                  value={
                    touchpointAnsweredAt
                      ? (() => {
                          const date = new Date(touchpointAnsweredAt);
                          const year = date.getFullYear();
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const day = String(date.getDate()).padStart(2, '0');
                          const hours = String(date.getHours()).padStart(2, '0');
                          const minutes = String(date.getMinutes()).padStart(2, '0');
                          return `${year}-${month}-${day}T${hours}:${minutes}`;
                        })()
                      : ''
                  }
                  onChange={e => {
                    if (e.target.value) {
                      // Convert local datetime to UTC ISO string with 6-digit microseconds
                      // Backend expects format: Y-m-d\TH:i:s.u\Z (e.g., 2025-12-06T00:18:00.000000Z)
                      const date = new Date(e.target.value);
                      const isoBase = date.toISOString(); // Returns: 2025-12-06T00:18:00.000Z
                      // Replace 3-digit milliseconds with 6-digit microseconds
                      const isoString = isoBase.replace(/(\.\d{3})Z$/, '$1000Z');
                      onTouchpointTimeChange(isoString);
                    }
                  }}
                  min={new Date().toISOString().split('T')[0] + 'T00:00'}
                  required
                  disabled={isReadOnly}
                  readOnly={isReadOnly}
                  className="text-xs w-auto min-w-[200px] min-h-[44px] h-11 px-3 touch-manipulation"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Assessment Titles and Standards - Mobile Optimized */}
        {titles.map(title => (
          <Card key={title.id} className="mb-4 sm:mb-6">
            <CardHeader className="bg-primary text-primary-foreground p-3 sm:p-6">
              <CardTitle className="text-base sm:text-lg">
                {title.name}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {title.standards.map((standard, index) => (
                <div
                  key={standard.id}
                  className="border-b border-gray-200 last:border-b-0"
                >
                  {/* Mobile-first responsive layout */}
                  <div className="p-3 sm:p-4 space-y-3">
                    {/* Question Section */}
                    <div
                      className={cn(
                        'flex items-start gap-2',
                        isRTL ? 'flex-row-reverse' : 'flex-row'
                      )}
                    >
                      <span className="font-medium text-gray-900 flex-shrink-0">
                        {index + 1}.
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-900 mb-2 break-words">
                          {standard.question}
                        </p>
                        <Badge
                          variant="outline"
                          className={`text-xs ${getSubTouchpointColor(standard.subTouchpointTag)} border-0`}
                        >
                          {standard.subTouchpointTag}
                        </Badge>
                      </div>
                    </div>

                    {/* Action Buttons - Mobile First Design */}
                    <div className="space-y-3">
                      {/* Status Buttons - Touch-friendly mobile design */}
                      <div className="grid grid-cols-3 gap-2 sm:flex sm:gap-1">
                        <Button
                          size="sm"
                          variant={
                            standard.status === 'yes' ? 'default' : 'outline'
                          }
                          className={`min-h-[44px] h-11 text-xs font-medium touch-manipulation transition-all ${
                            standard.status === 'yes'
                              ? 'bg-green-500 hover:bg-green-600 active:bg-green-700 text-white border-green-500 shadow-sm'
                              : 'hover:bg-green-50 hover:border-green-200 active:bg-green-100'
                          }`}
                          onClick={() =>
                            onStandardStatusChange(title.id, standard.id, 'yes')
                          }
                          disabled={isReadOnly}
                        >
                          ✓ {t('assessments.perform.yes')}
                        </Button>
                        <Button
                          size="sm"
                          variant={
                            standard.status === 'no' ? 'default' : 'outline'
                          }
                          className={`min-h-[44px] h-11 text-xs font-medium touch-manipulation transition-all ${
                            standard.status === 'no'
                              ? 'bg-red-500 hover:bg-red-600 active:bg-red-700 text-white border-red-500 shadow-sm'
                              : 'hover:bg-red-50 hover:border-red-200 active:bg-red-100'
                          }`}
                          onClick={() =>
                            onStandardStatusChange(title.id, standard.id, 'no')
                          }
                          disabled={isReadOnly}
                        >
                          ✗ {t('assessments.perform.no')}
                        </Button>
                        <Button
                          size="sm"
                          variant={
                            standard.status === 'na' ? 'default' : 'outline'
                          }
                          className={`min-h-[44px] h-11 text-xs font-medium touch-manipulation transition-all ${
                            standard.status === 'na'
                              ? 'bg-gray-500 hover:bg-gray-600 active:bg-gray-700 text-white border-gray-500 shadow-sm'
                              : 'hover:bg-gray-50 hover:border-gray-200 active:bg-gray-100'
                          }`}
                          onClick={() =>
                            onStandardStatusChange(title.id, standard.id, 'na')
                          }
                          disabled={isReadOnly}
                        >
                          — {t('assessments.perform.na')}
                        </Button>
                      </div>

                      {/* Notes Input - Touch-friendly */}
                      <Input
                        placeholder={t('assessments.perform.addNotes')}
                        value={standard.notes}
                        onChange={e =>
                          onStandardNotesChange(
                            title.id,
                            standard.id,
                            e.target.value
                          )
                        }
                        disabled={isReadOnly}
                        readOnly={isReadOnly}
                        className="text-sm w-full min-h-[44px] h-11 px-3 touch-manipulation"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
